/**
 * Central auth module — single source of truth for token management.
 * All axios instances (old service files + api/client.js) use this.
 */
import axios from "axios";
import env from "../config/env";
import { getOAuthHeaders } from "../constants/oAuthValidation";

// --- Cookie/token helpers ---

export const parseCookies = () => {
  const cookies = {};
  document.cookie.split("; ").forEach((c) => {
    const [name, ...rest] = c.split("=");
    cookies[name] = rest.join("=");
  });
  return cookies;
};

export const getAccessToken = () => {
  const cookies = parseCookies();
  for (const name of Object.keys(cookies)) {
    if (name.includes("accessToken")) {
      return cookies[name];
    }
  }
  return null;
};

const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
};

export const isTokenExpired = (token) => {
  const decoded = parseJwt(token);
  if (!decoded) return true;
  return decoded.exp * 1000 < Date.now();
};

const setCookie = (name, value, maxAgeSeconds = 3600) => {
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const domain = !isLocalhost && env.domain ? `domain=${env.domain};` : "";
  const secure = isLocalhost ? "" : "secure=true;";
  document.cookie = `${name}=${value}; max-age=${maxAgeSeconds}; path=/; ${secure} SameSite=Strict; ${domain}`;
};

// --- Token refresh (promise-deduplicated) ---

let refreshPromise = null;

export const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken || !env.refreshTokenUrl) {
        window.electronAPI?.auth?.logout();
        window.location.assign("/sign-in");
        return null;
      }

      const res = await axios.post(
        env.refreshTokenUrl,
        { refreshToken },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.status === 200 && res.data.accessToken) {
        setCookie("accessToken", res.data.accessToken);
        setCookie("Enterprise.accessToken", res.data.accessToken);
        setCookie("Enterprise.refreshToken", res.data.refreshToken);
        // Propagate refreshed token to all webview partitions via authBus
        window.electronAPI?.auth?.tokenRefreshed({
          accessToken: res.data.accessToken,
          refreshToken: res.data.refreshToken,
        });
        return res.data.accessToken;
      }

      window.electronAPI?.auth?.logout();
      window.location.assign("/sign-in");
      return null;
    } catch {
      window.electronAPI?.auth?.logout();
      window.location.assign("/sign-in");
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// --- Interceptors ---

/**
 * Request interceptor: attach current access token to outgoing requests.
 */
const attachAuthToken = (config) => {
  const authHeaders = getOAuthHeaders() || {};
  const { access_token } = authHeaders;
  if (access_token) {
    config.headers.Authorization = /^bearer /i.test(access_token) ? access_token : `Bearer ${access_token}`;
  }
  return config;
};

/**
 * Response interceptor: on 401, refresh token and retry the failed request once.
 * _retried flag prevents infinite retry loops.
 */
const handleAuthError = async (error) => {
  const status = error?.response?.status;
  const msg = error?.message || "";

  if (status === 401 || msg.includes("Unexpected token <") || msg.includes("status code 401")) {
    if (!error.config?._retried) {
      const newToken = await refreshAccessToken();
      if (newToken && error.config) {
        error.config._retried = true;
        error.config.headers.Authorization = `Bearer ${newToken}`;
        return axios(error.config);
      }
    }
  }
  return Promise.reject(error);
};

/**
 * Apply standard auth interceptors to an axios instance.
 * Call this after any tenant baseURL interceptors.
 */
export const applyAuthInterceptors = (instance) => {
  instance.interceptors.request.use(attachAuthToken, (err) => Promise.reject(err));
  instance.interceptors.response.use((res) => res, handleAuthError);
};
